import { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { resizeImageToSquare } from "@/lib/imageResize";
import type { Subject } from "@/lib/supabase";
import { invalidateChildren } from "@/hooks/useChildren";
import ChildAvatar from "./ChildAvatar";
import AvatarCropper, { applyExifOrientation } from "./AvatarCropper";
import AvatarSourcePicker from "./AvatarSourcePicker";

interface AvatarUploadProps {
  child: Subject;
  onUploadComplete: (newAvatarUrl: string) => void;
}

/**
 * Full avatar upload flow:
 * 1. User taps the 80px avatar → file picker opens (camera or gallery on mobile)
 * 2. User picks/shoots photo → AvatarCropper opens full screen
 * 3. User crops → taps "Use photo"
 * 4. getCroppedImg returns a 400x400 JPEG Blob
 * 5. Uploads to `avatars/{user_id}/{child_id}.jpg` (upsert)
 * 6. Updates `children.avatar_url` and calls onUploadComplete
 */
const AvatarUpload = ({ child, onUploadComplete }: AvatarUploadProps) => {
  const { t } = useTranslation();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const dataUrl = await applyExifOrientation(file);
      setImageSrc(dataUrl);
    } catch (err) {
      console.error("[AvatarUpload] file read failed", err);
      toast.error(t("toast.avatarReadError"));
    }
  };

  const handleCropComplete = async (blob: Blob) => {
    setUploading(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");

      const path = `${user.id}/${child.id}.jpg`;

      // Resize to 200x200 before upload — keeps file under 30KB
      const resized = await resizeImageToSquare(blob, 200);

      // Upload with upsert so re-uploads replace cleanly (no orphaned files)
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(path, resized, {
          contentType: "image/jpeg",
          upsert: true,
          cacheControl: "3600",
        });

      if (uploadError) throw uploadError;

      // Store the storage path — ChildAvatar generates signed URLs at display time.
      // Append a cache-buster so ChildAvatar's useEffect re-runs on the new path.
      const newAvatarUrl = `${path}?v=${Date.now()}`;

      const { data: updated, error: updateError } = await supabase
        .from("children")
        .update({ avatar_url: newAvatarUrl })
        .eq("id", child.id)
        .select()
        .single();

      if (updateError) throw updateError;
      if (!updated) throw new Error("Update returned no row — RLS likely blocked");

      toast.success(t("toast.avatarUpdateSuccess"));
      onUploadComplete(newAvatarUrl);
      void invalidateChildren();
      setImageSrc(null);
    } catch (err) {
      console.error("[AvatarUpload]", err);
      toast.error(t("toast.genericError"));
      // Stay on cropper so user can retry
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (uploading) return;
    setImageSrc(null);
  };

  return (
    <>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={handleFileChange}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={handleFileChange}
      />

      <AvatarSourcePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onCamera={() => cameraInputRef.current?.click()}
        onGallery={() => galleryInputRef.current?.click()}
      />

      <div className="flex flex-col items-center gap-1">
      <div className="relative inline-block">
        <ChildAvatar
          child={child}
          size={80}
          showEditRing
          onClick={() => setPickerOpen(true)}
        />
        {/* Camera badge bottom-right */}
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          aria-label={t("avatar.changePhoto")}
          className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md border-2 border-background transition-transform active:scale-95"
        >
          {uploading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Camera className="h-3.5 w-3.5" />
          )}
        </button>

        {/* Uploading overlay on the avatar itself */}
        {uploading && (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-full"
            style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
          >
            <Loader2 className="h-6 w-6 animate-spin text-white" />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {t("button.changePhoto")}
      </button>
      </div>

      {imageSrc && (
        <AvatarCropper
          imageSrc={imageSrc}
          onComplete={handleCropComplete}
          onCancel={handleCancel}
          uploading={uploading}
        />
      )}
    </>
  );
};

export default AvatarUpload;
